import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import type { ReactNode } from "react";
import { AppearanceProvider } from "./lib/appearance";
import { AuthProvider, useAuth } from "./lib/auth-session";
import { NotifyProvider } from "./lib/notify-store";
import { RoleProvider } from "./lib/roles";
import { PageSkeleton } from "./components/PageSkeleton";
import { Shell } from "./components/Shell";
import { BroadcastsPage } from "./pages/BroadcastsPage";
import { BroadcastDetailPage } from "./pages/BroadcastDetailPage";
import { AutomationsPage } from "./pages/AutomationsPage";
import { DesignStudioPage } from "./pages/DesignStudioPage";
import { ContentPage } from "./pages/ContentPage";
import { NewsletterEditorPage, NotFound } from "./pages/NewsletterEditorPage";
import { OverviewPage } from "./pages/OverviewPage";
import { PipelinePage } from "./pages/PipelinePage";
import { RoadmapPage } from "./pages/RoadmapPage";
import { SegmentsPage } from "./pages/SegmentsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SubscribersPage } from "./pages/SubscribersPage";
import { TeamPage } from "./pages/TeamPage";
import { HealthPage } from "./pages/HealthPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { InvitePage, LoginPage } from "./pages/LoginPage";

const qc = new QueryClient();

function InviteRoute() {
  const { token } = useParams<{ token: string }>();
  if (!token) return <Navigate to="/login" replace />;
  return <InvitePage token={token} />;
}

function Guard({ children }: { children: ReactNode }) {
  const { ready, session } = useAuth();
  const location = useLocation();
  if (!ready) return <PageSkeleton />;
  if (session.configured && !session.me) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <AppearanceProvider>
      <AuthProvider>
        <RoleProvider>
          <NotifyProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/invite/:token" element={<InviteRoute />} />
                <Route path="*" element={
                  <Guard>
                    <Shell>
                      <Routes>
                        <Route path="/" element={<OverviewPage />} />
                        <Route path="/pipeline" element={<PipelinePage />} />
                        <Route path="/subscribers" element={<SubscribersPage />} />
                        <Route path="/audience" element={<SegmentsPage />} />
                        <Route path="/segments" element={<Navigate to="/audience" replace />} />
                        <Route path="/content" element={<ContentPage />} />
                        <Route path="/content/:issueId" element={<NewsletterEditorPage />} />
                        <Route path="/broadcasts" element={<BroadcastsPage />} />
                        <Route path="/broadcasts/:id" element={<BroadcastDetailPage />} />
                        <Route path="/automations" element={<AutomationsPage />} />
                        <Route path="/design" element={<DesignStudioPage />} />
                        {/* Campaigns only ever showed sample data; Broadcasts is the real thing. */}
                        <Route path="/campaigns" element={<Navigate to="/broadcasts" replace />} />
                        <Route path="/analytics" element={<AnalyticsPage />} />
                        <Route path="/roadmap" element={<RoadmapPage />} />
                        <Route path="/health" element={<HealthPage />} />
                        <Route path="/team" element={<TeamPage />} />
                        <Route path="/settings" element={<SettingsPage />} />
                        {/* Without this, an address matching no route rendered
                            the shell around an empty middle. */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Shell>
                  </Guard>
                } />
              </Routes>
            </BrowserRouter>
          </NotifyProvider>
        </RoleProvider>
      </AuthProvider>
      </AppearanceProvider>
    </QueryClientProvider>
  );
}
